import { kaspiPOSService } from './kaspiPosService';
import { halykPOSService } from './halykPosService';
import { freedomPOSService } from './freedomPosService';

/**
 * Универсальный сервис для работы с терминалами
 */
class TerminalIntegrationService {
    constructor() {
        this.currentProcessId = '';
        this.isProcessing = false;
    }

    /**
     * Получить сервис для конкретного типа терминала
     * @param {string} posType - Тип терминала (kaspi, halyk)
     * @returns {Object} Сервис для работы с терминалом
     */
    getTerminalService(posType) {
        switch (posType) {
            case 'kaspi':
                return kaspiPOSService;
            case 'halyk':
                return halykPOSService;
            case 'freedom':
                return freedomPOSService;
            default:
                throw new Error(`Неподдерживаемый тип терминала: ${posType}`);
        }
    }

    /**
     * Определяет тип сценария для терминала
     * @param {string} posType - Тип терминала
     * @returns {string} 'sync' для Halyk, 'async' для Kaspi
     */
    getTerminalScenario(posType) {
        switch (posType) {
            case 'kaspi':
                return 'async'; // Запрос → Мониторинг статуса
            case 'halyk':
                return 'sync';  // Запрос → Сразу результат
            case 'freedom':
                return 'sync';  // Freedom работает как Halyk - сразу результат
            default:
                return 'async';
        }
    }

    /**
     * Запуск платежа через терминал
     * @param {Object} paymentData - Данные для платежа
     * @param {string} paymentData.posType - Тип терминала
     * @param {number} paymentData.amount - Сумма платежа
     * @param {Function} statusCallback - Callback для обновления статуса
     * @returns {Promise<Object>} Результат платежа
     */
    async processPayment(paymentData, statusCallback) {
        const { posType, amount } = paymentData;

        if (this.isProcessing) {
            throw new Error('Платеж уже обрабатывается');
        }

        const configCheck = await this.checkTerminalConfig(posType);
        if (!configCheck.success) {
            throw new Error(configCheck.error);
        }

        this.isProcessing = true;
        statusCallback('Начинаем оплату...');

        try {
            const terminalService = this.getTerminalService(posType);
            const scenario = this.getTerminalScenario(posType);

            // Запуск платежа
            const result = await terminalService.startPayment(parseInt(amount), true);

            if (result.success) {
                this.currentProcessId = result.processId;

                if (scenario === 'sync') {
                    // Halyk: результат уже получен
                    if (result.status === 'success') {
                        statusCallback('Оплата успешна!');
                        return {
                            success: true,
                            paymentDetails: result,
                            message: 'Платеж успешно завершен'
                        };
                    } else {
                        statusCallback(result.error || 'Оплата не удалась');
                        return {
                            success: false,
                            error: result.error || 'Неизвестная ошибка при оплате'
                        };
                    }
                } else {
                    // Kaspi: мониторинг статуса
                    statusCallback('Оплата началась, ожидаем завершения...');

                    const finalResult = await terminalService.monitorTransaction(
                        result.processId,
                        (statusUpdate) => {
                            statusCallback(statusUpdate.message || 'Обработка платежа...');
                        }
                    );

                    if (finalResult.success) {
                        if (finalResult.status === 'success') {
                            statusCallback('Оплата успешна!');
                            return {
                                success: true,
                                paymentDetails: finalResult,
                                message: 'Платеж успешно завершен'
                            };
                        } else {
                            statusCallback(finalResult.message || 'Оплата не удалась');
                            return {
                                success: false,
                                error: finalResult.message || 'Неизвестная ошибка при оплате'
                            };
                        }
                    } else {
                        return {
                            success: false,
                            error: finalResult.error || 'Ошибка мониторинга платежа'
                        };
                    }
                }
            } else {
                statusCallback(result.error || 'Ошибка запуска платежа');
                return {
                    success: false,
                    error: result.error || 'Ошибка запуска платежа'
                };
            }
        } catch (error) {
            statusCallback('Ошибка при оплате');
            return {
                success: false,
                error: error.message || 'Неизвестная ошибка'
            };
        } finally {
            this.isProcessing = false;
            this.currentProcessId = '';
        }
    }

    /**
     * Запуск возврата через терминал
     * @param {Object} refundData - Данные для возврата
     * @param {string} refundData.posType - Тип терминала
     * @param {number} refundData.amount - Сумма возврата
     * @param {Object} refundData.transactionData - Данные оригинальной транзакции
     * @param {Function} statusCallback - Callback для обновления статуса
     * @returns {Promise<Object>} Результат возврата
     */
    async processRefund(refundData, statusCallback) {
        const { posType, amount, transactionData } = refundData;

        if (this.isProcessing) {
            throw new Error('Возврат уже обрабатывается');
        }

        const configCheck = await this.checkTerminalConfig(posType);
        if (!configCheck.success) {
            throw new Error(configCheck.error);
        }

        this.isProcessing = true;
        statusCallback('Начинаем возврат...');

        try {
            const terminalService = this.getTerminalService(posType);
            const scenario = this.getTerminalScenario(posType);

            // Запуск возврата
            const result = await terminalService.startRefund(parseInt(amount), transactionData, true);

            if (result.success) {
                this.currentProcessId = result.processId;

                if (scenario === 'sync') {
                    // Halyk: результат уже получен
                    if (result.status === 'success') {
                        statusCallback('Возврат успешен!');
                        return {
                            success: true,
                            refundDetails: result,
                            message: 'Возврат успешно завершен'
                        };
                    } else {
                        statusCallback(result.error || 'Возврат не удался');
                        return {
                            success: false,
                            error: result.error || 'Неизвестная ошибка при возврате'
                        };
                    }
                } else {
                    // Kaspi: мониторинг статуса
                    statusCallback('Возврат начался, ожидаем завершения...');

                    const finalResult = await terminalService.monitorTransaction(
                        result.processId,
                        (statusUpdate) => {
                            statusCallback(statusUpdate.message || 'Обработка возврата...');
                        }
                    );

                    if (finalResult.success) {
                        if (finalResult.status === 'success') {
                            statusCallback('Возврат успешен!');
                            return {
                                success: true,
                                refundDetails: finalResult,
                                message: 'Возврат успешно завершен'
                            };
                        } else {
                            statusCallback(finalResult.message || 'Возврат не удался');
                            return {
                                success: false,
                                error: finalResult.message || 'Неизвестная ошибка при возврате'
                            };
                        }
                    } else {
                        return {
                            success: false,
                            error: finalResult.error || 'Ошибка мониторинга возврата'
                        };
                    }
                }
            } else {
                statusCallback(result.error || 'Ошибка запуска возврата');
                return {
                    success: false,
                    error: result.error || 'Ошибка запуска возврата'
                };
            }
        } catch (error) {
            statusCallback('Ошибка при возврате');
            return {
                success: false,
                error: error.message || 'Неизвестная ошибка'
            };
        } finally {
            this.isProcessing = false;
            this.currentProcessId = '';
        }
    }

    /**
     * Отмена текущего платежа
     */
    async cancelPayment() {
        if (this.currentProcessId) {
            try {
                this.currentProcessId = '';
                this.isProcessing = false;
            } catch (error) {
                console.error('Ошибка отмены платежа:', error);
            }
        }
    }

    /**
     * Проверка доступности терминала
     * @param {string} posType - Тип терминала
     * @returns {Promise<boolean>} Доступность терминала
     */
    async isTerminalAvailable(posType) {
        try {
            const terminalService = this.getTerminalService(posType);
            return true;
        } catch (error) {
            console.error('Ошибка проверки доступности терминала:', error);
            return false;
        }
    }

    /**
     * Проверка конфигурации терминала
     * @param {string} posType - Тип терминала
     * @returns {Promise<Object>} Результат проверки
     */
    async checkTerminalConfig(posType) {
        try {
            const terminalService = this.getTerminalService(posType);

            // Проверяем авторизацию устройства
            if (!terminalService.checkDeviceAuthorization()) {
                return {
                    success: false,
                    error: 'Устройство не авторизовано'
                };
            }

            // Проверяем инициализацию
            const initialized = await terminalService.init();
            if (!initialized) {
                return {
                    success: false,
                    error: `Терминал ${posType} не настроен или IP адрес не найден`
                };
            }

            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }
}

export const terminalIntegrationService = new TerminalIntegrationService();